import type React from 'react';
import {
  Circle as CircleIcon,
  PowerSettingsNew as BootIcon,
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { colors } from '@/theme/tokens';

/**
 * Event classification, kept out of device-events.tsx so it can be shared with
 * the Analytics container (verdict + chart markers) without tripping
 * react-refresh's one-component-per-file rule.
 */

export type Severity = 'routine' | 'notable' | 'fault';

// Severity → chart segment color/label, kept in lock-step with the list rows
// (notable = amber, fault = red) so the bar chart and the list read the same.
export const ACTIVITY_COLORS: Record<Severity, string> = {
  routine: colors.info.main,
  notable: colors.warning.main,
  fault: colors.error.main,
};
export const ACTIVITY_LABELS: Record<Severity, string> = {
  routine: 'Activity',
  notable: 'Notable',
  fault: 'Problem',
};

export function titleCase(s: string): string {
  return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function relativeTime(ts: string): string {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatEventTime(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export interface EventClass { label: string; severity: Severity; icon: React.ReactNode }

// Severity is derived from the event + its data — NEVER the raw type alone.
// A routine power-down (presence_offline / heartbeat_timeout) is neutral, not red.
export function classifyEvent(eventType: string, data: Record<string, unknown> | null): EventClass {
  const reason = data && typeof data.reason === 'string' ? data.reason : '';
  switch (eventType) {
    case 'boot':
      return { label: 'Turned on', severity: 'routine', icon: <BootIcon fontSize="small" sx={{ color: 'text.secondary' }} /> };
    case 'presence_online':
      return { label: 'Came online', severity: 'routine', icon: <OnlineIcon fontSize="small" sx={{ color: 'text.secondary' }} /> };
    case 'presence_offline':
      // heartbeat_timeout is the normal "machine switched off" path — routine.
      return {
        label: reason === 'heartbeat_timeout' ? 'Turned off' : 'Went offline',
        severity: 'routine',
        icon: <OfflineIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
      };
    case 'enrollment':
      return { label: 'Enrolled', severity: 'routine', icon: <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} /> };
    case 'software_update':
      return { label: 'Software update', severity: 'routine', icon: <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} /> };
    case 'status_change':
      return { label: 'Status changed', severity: 'notable', icon: <WarningIcon fontSize="small" color="warning" /> };
    case 'update_failed':
      return { label: 'Update failed', severity: 'fault', icon: <ErrorIcon fontSize="small" color="error" /> };
    default:
      return { label: titleCase(eventType), severity: 'routine', icon: <CircleIcon fontSize="small" sx={{ color: 'text.disabled' }} /> };
  }
}

